import React, { useCallback, useEffect, useState } from "react";
import { useChartsStore } from "~/store/chartStore";
import ChartWrapper from "../ChartWrapper/ChartWrapper";
import { useGroupLink } from "../../hooks/useGroupLink";

const ChartPanel = React.memo(
  ({ chartId, onInteractiveApiReady, layoutInvalidationKey, claimFocus }) => {
    const [isPointerInsideChartPanel, setIsPointerInsideChartPanel] =
      useState(false);
    // Espelha o state p/ um gate O(1) no onMouseMove (hot path): só chama
    // setState na transição false→true, evitando churn de re-render. (#272)
    const insideRef = React.useRef(false);
    const markInside = useCallback(() => {
      if (insideRef.current) return;
      insideRef.current = true;
      setIsPointerInsideChartPanel(true);
    }, []);
    const chart = useChartsStore((state) => state.getChart(chartId));
    const hasData = useChartsStore((state) => {
      const d = state.getData(chartId);
      return d?.length > 0;
    });
    const setFocusedChart = useChartsStore((state) => state.setFocusedChart);
    const changeChartSymbol = useChartsStore(
      (state) => state.changeChartSymbol,
    );

    // Track whether the symbol change came from group propagation to avoid re-emitting
    const isGroupUpdateRef = React.useRef(false);

    // Group linking - register as receiver for symbol changes
    // When another panel in the same group changes symbol, this chart will update
    const handleGroupSymbolChange = useCallback(
      (symbol) => {
        isGroupUpdateRef.current = true;
        // Group-link receiver (propagation) — not direct engagement.
        changeChartSymbol(chartId, symbol, { skipEngagement: true });
      },
      [chartId, changeChartSymbol],
    );

    const { emitSymbol } = useGroupLink(
      chartId,
      chart?.group,
      handleGroupSymbolChange,
    );

    // Emit symbol change when chart's symbol changes locally (e.g., via spotlight)
    // This propagates the change to other charts in the same group
    const prevSymbolRef = React.useRef(chart?.symbol);
    useEffect(() => {
      if (chart?.symbol && prevSymbolRef.current !== chart.symbol) {
        prevSymbolRef.current = chart.symbol;

        // Only emit if this is a local change (not from group propagation)
        if (!isGroupUpdateRef.current) {
          emitSymbol(chart.symbol);
        }
        isGroupUpdateRef.current = false;
      }
    }, [chart?.symbol, emitSymbol]);

    // Transfer active interactive tool when mouse enters a non-focused chart.
    // This gives React time to re-render before the user clicks to draw.
    const handleMouseEnter = useCallback(() => {
      markInside();
      const {
        focusedChartId: currentFocused,
        activeInteractiveTools,
        setActiveInteractive,
      } = useChartsStore.getState();
      if (
        currentFocused &&
        currentFocused !== chartId &&
        activeInteractiveTools[currentFocused]
      ) {
        const toolType = activeInteractiveTools[currentFocused];
        setActiveInteractive(currentFocused, null);
        // Transfer-on-hover — programmatic re-arm, not direct engagement.
        setActiveInteractive(chartId, toolType, { skipEngagement: true });
        setFocusedChart(chartId);
      }
    }, [chartId, setFocusedChart, markInside]);

    // Recuperação sem cruzar a fronteira: qualquer movimento do ponteiro DENTRO
    // do painel restaura os controles. Sem isto, ao fechar um modal (portal no
    // body) sob cursor parado o estado ficava preso em `false` — o navegador nem
    // sempre re-dispara o mouseenter sintético, então os botões de zoom só
    // voltavam ao sair e re-entrar no gráfico. (#272)
    const handleMouseMove = markInside;

    const handleMouseLeave = useCallback(() => {
      insideRef.current = false;
      setIsPointerInsideChartPanel(false);
    }, []);

    const handleFocus = useCallback(() => {
      // Claim FlexLayout focus (draws the active-tabset border + syncs focusedPanelType
      // via onModelChange → syncFocusFromModel). Guarded internally to a no-op when this
      // tabset is already active, so it doesn't churn on every chart interaction. (#347)
      claimFocus?.();
      // Only update focus if it actually changed — avoids unnecessary re-render
      const { focusedChartId: current } = useChartsStore.getState();
      if (current !== chartId) {
        setFocusedChart(chartId);
      }
    }, [chartId, setFocusedChart, claimFocus]);

    if (!chart) return null;

    return (
      <div
        className="chart-panel"
        // Lets a modal opened elsewhere (navbar, sidebar) find the window this
        // chart lives in — see ~/hooks/useChartHostBody (issue #375).
        data-chart-id={chartId}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleFocus}
        style={{ width: "100%", height: "100%" }}
      >
        {hasData || chart.symbol ? (
          <ChartWrapper
            chartId={chartId}
            onInteractiveApiReady={onInteractiveApiReady}
            layoutInvalidationKey={layoutInvalidationKey}
            isPointerInsideChartPanel={isPointerInsideChartPanel}
          />
        ) : (
          <div className="chart-panel-placeholder">
            <h1>Select a symbol</h1>
          </div>
        )}
      </div>
    );
  },
);

ChartPanel.displayName = "ChartPanel";
export default ChartPanel;
